import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TrendingUp, Sparkles, Loader2, MapPin, BarChart3, ArrowUpRight, Clock, Users, DollarSign } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { usePageMeta } from '../hooks/usePageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = [
  'Registered Nurse (RN)', 'Licensed Practical Nurse (LPN)', 'Certified Nursing Assistant (CNA)',
  'Travel Nursing', 'ICU / Critical Care', 'Emergency Medicine', 'Medical-Surgical',
  'Pediatric Nursing', 'Oncology Nursing', 'Home Health', 'Long-Term Care / SNF',
  'Physical Therapy', 'Occupational Therapy', 'Respiratory Therapy', 'Behavioral Health',
  'Telehealth', 'Allied Health', 'Physician Staffing',
];

const REGIONS = [
  'Northeast (NY, NJ, PA, CT, MA)', 'Southeast (FL, GA, NC, SC, VA)',
  'Midwest (IL, OH, MI, IN, WI)', 'Southwest (TX, AZ, NM, OK)',
  'West Coast (CA, WA, OR)', 'Mountain (CO, UT, NV, MT)',
  'Mid-Atlantic (MD, DE, DC)', 'New England (ME, NH, VT, RI)',
  'South Central (TN, AL, MS, LA, AR, KY)', 'Great Plains (MN, IA, MO, KS, NE, SD, ND)',
  'Pacific (HI, AK)', 'All United States',
];

const IMAGES = {
  banner: 'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=1200&h=400&fit=crop',
  side: 'https://images.unsplash.com/photo-1606318313647-137d1f3b4d3c?w=400&h=500&fit=crop',
  inline: 'https://images.unsplash.com/photo-1666886573419-aaaca27447de?w=1200&h=300&fit=crop',
};

const DEFAULT_CHART = {
  demand_trend: [
    { month: 'Jan', demand: 72 }, { month: 'Feb', demand: 75 }, { month: 'Mar', demand: 78 },
    { month: 'Apr', demand: 82 }, { month: 'May', demand: 86 }, { month: 'Jun', demand: 89 },
  ],
  top_regions: [
    { region: 'Florida', openings: 3200 }, { region: 'Texas', openings: 2800 },
    { region: 'California', openings: 2500 }, { region: 'New York', openings: 2100 },
    { region: 'Ohio', openings: 1700 },
  ],
  salary_range: { min: 55000, max: 95000, median: 72000 },
  supply_demand: [
    { category: 'Open Positions', value: 45000 },
    { category: 'Active Candidates', value: 32000 },
    { category: 'Placements/Mo', value: 8500 },
  ],
  growth_rate: 12.5, fill_rate: 71, avg_time_to_fill: 18,
};

// Simple Line Chart
const MiniLineChart = ({ data, dataKey, height = 180 }) => {
  const values = data.map(d => d[dataKey]);
  const max = Math.max(...values);
  const min = Math.min(...values) * 0.9;
  const range = max - min || 1;
  const w = 100;
  const h = 100;
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * w,
    y: h - ((d[dataKey] - min) / range) * h,
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${w} ${h} L 0 ${h} Z`;

  return (
    <div style={{ height }} data-testid="line-chart">
      <svg viewBox={`-5 -5 ${w + 10} ${h + 25}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(y => (
          <line key={y} x1={0} y1={y} x2={w} y2={y} stroke="hsl(var(--border))" strokeWidth="0.5" strokeDasharray="2,2" />
        ))}
        {/* Area fill */}
        <path d={areaD} fill="hsl(221, 84%, 57%)" opacity="0.08" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="hsl(221, 84%, 57%)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill="hsl(221, 84%, 57%)" />
            <text x={p.x} y={h + 14} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: '7px' }}>{data[i].month}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// Horizontal Bar Chart
const HBarChart = ({ data, labelKey, valueKey, height = 200 }) => {
  const max = Math.max(...data.map(d => d[valueKey]));
  return (
    <div className="space-y-3" style={{ minHeight: height }} data-testid="hbar-chart">
      {data.map((d, i) => {
        const pct = (d[valueKey] / max) * 100;
        const colors = ['bg-primary', 'bg-primary/80', 'bg-primary/65', 'bg-primary/50', 'bg-accent/60'];
        return (
          <div key={i}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium text-foreground">{d[labelKey]}</span>
              <span className="text-xs text-muted-foreground font-mono">{d[valueKey].toLocaleString()}</span>
            </div>
            <div className="h-5 bg-muted rounded-md overflow-hidden">
              <div
                className={`h-full rounded-md transition-all duration-700 ease-out ${colors[i % colors.length]}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Vertical Bar Chart
const VBarChart = ({ data, labelKey, valueKey, height = 180 }) => {
  const max = Math.max(...data.map(d => d[valueKey]));
  const colors = ['bg-primary', 'bg-primary/75', 'bg-accent/70'];
  return (
    <div className="flex items-end gap-4 justify-center" style={{ height }} data-testid="vbar-chart">
      {data.map((d, i) => {
        const pct = (d[valueKey] / max) * 100;
        return (
          <div key={i} className="flex flex-col items-center gap-2 flex-1">
            <span className="text-xs font-bold text-foreground">{(d[valueKey] / 1000).toFixed(1)}K</span>
            <div className="w-full max-w-[48px] bg-muted rounded-t-md overflow-hidden" style={{ height: `${Math.max(pct, 10)}%` }}>
              <div className={`w-full h-full ${colors[i % colors.length]} rounded-t-md transition-all duration-700`} />
            </div>
            <span className="text-[10px] text-muted-foreground text-center leading-tight">{d[labelKey]}</span>
          </div>
        );
      })}
    </div>
  );
};

export const DemandForecastPage = () => {
  usePageMeta({
    title: 'AI Demand Forecast - Healthcare Staffing Market Intelligence',
    description: 'Get AI-powered healthcare staffing demand forecasts with market trends, salary data, regional hotspots, and strategic recommendations for your specialty.',
    path: '/tools/demand-forecast',
  });

  const [specialty, setSpecialty] = useState('');
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/demand-forecast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ specialty: specialty || null, region: region || null }),
      });
      if (!res.ok) throw new Error('Failed');
      setResult(await res.json());
    } catch (err) {
      setError('Unable to generate forecast right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const chart = result?.chart_data || (result ? DEFAULT_CHART : null);

  const splitContent = (content) => {
    if (!content) return { top: '', bottom: '' };
    const lines = content.split('\n');
    const mid = Math.floor(lines.length * 0.5);
    return { top: lines.slice(0, mid).join('\n'), bottom: lines.slice(mid).join('\n') };
  };
  const sections = result ? splitContent(result.content) : null;

  return (
    <div className="min-h-screen bg-background" data-testid="demand-forecast-page">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="container-width text-center relative">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6 text-sm font-medium">
            <BarChart3 className="h-4 w-4" />
            Market Intelligence
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">Demand Forecasting</h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            AI-driven analysis of healthcare staffing demand. Understand market trends, salary benchmarks, and regional hotspots.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-width max-w-6xl">
          {/* Form */}
          <Card className="p-6 md:p-8 border-border/50 mb-8" data-testid="forecast-form">
            <h2 className="text-lg font-bold text-foreground tracking-tight mb-1">Configure your analysis</h2>
            <p className="text-sm text-muted-foreground mb-6">Choose a specialty and region for a targeted market forecast.</p>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Specialty</label>
                  <select value={specialty} onChange={e => setSpecialty(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="forecast-specialty-select">
                    <option value="">All Specialties</option>
                    {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">Region</label>
                  <select value={region} onChange={e => setRegion(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="forecast-region-select">
                    <option value="">All Regions</option>
                    {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="bg-primary text-white rounded-full px-8 h-11 hover:translate-y-[-1px] transition-transform" data-testid="forecast-generate-btn">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing Market...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Forecast</>}
              </Button>
            </form>
          </Card>

          {error && <Card className="p-4 mb-6 border-red-500/20 bg-red-500/5 text-red-600 dark:text-red-400 text-sm" data-testid="forecast-error">{error}</Card>}

          {loading && (
            <Card className="p-16 text-center border-border/50" data-testid="forecast-loading">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Loader2 className="h-7 w-7 text-primary animate-spin" />
              </div>
              <p className="text-foreground font-medium mb-1">Analyzing market data...</p>
              <p className="text-sm text-muted-foreground">Compiling demand trends, salary benchmarks, and regional insights.</p>
            </Card>
          )}

          {/* Results Dashboard */}
          {result && !loading && (
            <div className="space-y-6" data-testid="forecast-result">
              {/* Banner */}
              <div className="rounded-2xl overflow-hidden shadow-lg" data-testid="forecast-banner-image">
                <img src={IMAGES.banner} alt="Market analytics" className="w-full h-48 md:h-56 object-cover" />
              </div>

              {/* KPI Cards */}
              {chart && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="forecast-kpi-cards">
                  <Card className="p-5 border-border/50">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-2xl font-bold text-foreground tracking-tight">+{chart.growth_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-0.5">YoY Growth</p>
                  </Card>
                  <Card className="p-5 border-border/50">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-2">
                      <ArrowUpRight className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <p className="text-2xl font-bold text-foreground tracking-tight">{chart.fill_rate}%</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Fill Rate</p>
                  </Card>
                  <Card className="p-5 border-border/50">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center mb-2">
                      <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-2xl font-bold text-foreground tracking-tight">{chart.avg_time_to_fill}d</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Time to Fill</p>
                  </Card>
                  <Card className="p-5 border-border/50">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                      <DollarSign className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-2xl font-bold text-foreground tracking-tight">${(chart.salary_range.median / 1000).toFixed(0)}K</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Median Salary</p>
                  </Card>
                </div>
              )}

              {/* Charts Row */}
              {chart && (
                <div className="grid md:grid-cols-2 gap-6" data-testid="forecast-charts">
                  <Card className="p-6 border-border/50">
                    <h3 className="text-sm font-bold text-foreground tracking-tight mb-1">6-Month Demand Index</h3>
                    <p className="text-xs text-muted-foreground mb-3">Projected demand trajectory</p>
                    <MiniLineChart data={chart.demand_trend} dataKey="demand" />
                  </Card>
                  <Card className="p-6 border-border/50">
                    <h3 className="text-sm font-bold text-foreground tracking-tight mb-1">Top Regions by Openings</h3>
                    <p className="text-xs text-muted-foreground mb-3">Active job openings by state</p>
                    <HBarChart data={chart.top_regions} labelKey="region" valueKey="openings" />
                  </Card>
                </div>
              )}

              {/* Supply/Demand + Salary + Image */}
              {chart && (
                <div className="grid md:grid-cols-3 gap-6" data-testid="forecast-stats-row">
                  <Card className="p-6 border-border/50">
                    <h3 className="text-sm font-bold text-foreground tracking-tight mb-1">Supply vs Demand</h3>
                    <p className="text-xs text-muted-foreground mb-4">Market balance</p>
                    <VBarChart data={chart.supply_demand} labelKey="category" valueKey="value" height={160} />
                  </Card>
                  <Card className="p-6 border-border/50">
                    <h3 className="text-sm font-bold text-foreground tracking-tight mb-1">Salary Range</h3>
                    <p className="text-xs text-muted-foreground mb-6">Annual compensation</p>
                    <div className="space-y-5">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary tracking-tight">${(chart.salary_range.median / 1000).toFixed(0)}K</p>
                        <p className="text-xs text-muted-foreground mt-1">Median Annual</p>
                      </div>
                      <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/30 via-primary to-primary/30 rounded-full" />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full border-2 border-background shadow-md"
                          style={{ left: `${((chart.salary_range.median - chart.salary_range.min) / (chart.salary_range.max - chart.salary_range.min)) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>${(chart.salary_range.min / 1000).toFixed(0)}K min</span>
                        <span>${(chart.salary_range.max / 1000).toFixed(0)}K max</span>
                      </div>
                    </div>
                  </Card>
                  <Card className="border-border/50 overflow-hidden hidden md:block" data-testid="forecast-side-image">
                    <img src={IMAGES.side} alt="Healthcare workforce" className="w-full h-full object-cover" />
                  </Card>
                </div>
              )}

              {/* AI Analysis Content */}
              <Card className="border-border/50 overflow-hidden" data-testid="forecast-content-card">
                <div className="px-6 md:px-8 py-5 bg-secondary/50 border-b border-border/50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-foreground tracking-tight">{specialty || 'Healthcare'} Staffing Forecast</h2>
                    <p className="text-xs text-muted-foreground">{region || 'United States'} &middot; AI Market Analysis</p>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-muted-foreground prose-li:leading-relaxed dark:prose-invert" data-testid="forecast-content">
                    <ReactMarkdown>{sections.top}</ReactMarkdown>
                  </div>
                </div>
                <div className="px-6 md:px-8 py-2">
                  <div className="rounded-xl overflow-hidden shadow-md" data-testid="forecast-inline-image">
                    <img src={IMAGES.inline} alt="Healthcare staffing" className="w-full h-36 md:h-44 object-cover" />
                  </div>
                </div>
                <div className="p-6 md:p-8 pt-4">
                  <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight prose-p:text-muted-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-li:text-muted-foreground prose-li:leading-relaxed dark:prose-invert">
                    <ReactMarkdown>{sections.bottom}</ReactMarkdown>
                  </div>
                </div>
                <div className="px-6 md:px-8 py-6 bg-secondary/50 border-t border-border/50">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Need staffing support?</p>
                        <p className="text-xs text-muted-foreground">Connect with our recruitment team</p>
                      </div>
                    </div>
                    <a href="/find-talent">
                      <Button className="bg-primary text-white rounded-full px-6 hover:translate-y-[-1px] transition-transform" data-testid="forecast-find-talent-btn">Find Talent</Button>
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {!result && !loading && !error && (
            <Card className="p-16 text-center border-dashed border-2 border-border/50">
              <TrendingUp className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-foreground font-medium mb-1">Explore market demand</p>
              <p className="text-sm text-muted-foreground">Select filters above to generate an AI-powered staffing demand forecast.</p>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DemandForecastPage;
